import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { registerViewPane } from 'tabview/assistantShared/registerViewPane';
import { ViewLegend, type ViewLegendRowModel } from '@ardoq/view-legend';
import { ViewLegendContainer } from '@ardoq/graph';
import { noop } from 'lodash';
import type { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { colors } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { t } from '@ardoq/i18n';
import { formatNumber } from '@ardoq/locale';
import {
  ContourChart,
  resolveCssColor,
  type ContourChartProps,
  type ContourViewSettings,
} from '@ardoq/xyz-charts';
import styled from 'styled-components';
import { VisualizationContainer } from 'tabview/ViewContainer';
import type { ContourSourceData } from './sourceData/buildContourSourceData';
import type { ContourViewProperties } from './types';
import {
  TooFewPositionsDoq,
  ChartEmptyState,
} from 'tabview/chartShared/ChartEmptyState';

/** See `Surface3DView` for why `min-height: 0` is what keeps the canvas from collapsing. */
const ContourLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ChartArea = styled(VisualizationContainer)`
  overflow: hidden;
  min-height: 0;
`;

/* The settings arrive beside the derived data rather than inside it: the builder is handed only
   the five field names, so a ramp or an isoline toggle cannot re-derive the points. Same split as
   `Surface3DViewProps`. */
export type ContourViewProps = ContourSourceData & {
  viewSettings: ContourViewSettings;
  legendRows: ViewLegendRowModel[];
  viewLegendLabels: ReturnType<typeof getViewLegendLabels>;
};

const useChartColors = (element: HTMLElement | null) =>
  useMemo(
    () => ({
      surfaceColor: resolveCssColor(element, colors.surfaceDefault, '#ffffff'),
      inkColor: resolveCssColor(element, colors.textDefault, '#1F2126'),
      gridColor: resolveCssColor(element, colors.borderSubtle, '#DADADA'),
    }),
    [element]
  );

export const ContourView = ({
  points,
  axes,
  viewSettings,
  xAxisTitle,
  yAxisTitle,
  valueTitle,
  isViewEmpty,
  emptyReason,
  excludedCount,
  numericFields,
  viewInstanceId,
  locale,
  i18nLocale,
  coloring,
  legendRows,
  viewLegendLabels,
}: ContourViewProps) => {
  /* State behind a callback ref, not a `useRef`, and that is the difference between the colors
     resolving and not. A ref is populated after commit, so reading `.current` during render hands
     `useChartColors` a null on the render that mounts the layout - and `resolveCssColor` then
     returns its hardcoded light fallbacks. Nothing re-renders this view when the ref attaches, so a
     dark theme kept a white panel with near-black ink on it until some unrelated stream emission
     happened along. `setContainer` is a state update, so the colors are resolved against the live
     element on the very next render. Same fix as `Surface3DView`. */
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const chartColors = useChartColors(container);

  /* Optimistic, and corrected by the chart's first grid. Starting at `false` would disable the
     export menu for the frame before the chart has answered. Same shape as `Surface3DView`, and
     for the same failure: the gridding can come back empty on source data this view reads as
     perfectly plottable, and in that state there is no canvas to export. */
  const [isDrawable, setIsDrawable] = useState(true);

  /* The chart publishes its export hook once it has a canvas. Held in a ref and read through a
     stable closure so the registration below does not have to re-run when it arrives. */
  const createExportCanvasRef = useRef<(() => OffscreenCanvas | null) | null>(
    null
  );
  const onExportCanvasAvailable = useCallback(
    (createExportCanvas: (() => OffscreenCanvas | null) | null) => {
      createExportCanvasRef.current = createExportCanvas;
    },
    []
  );

  /* `registerViewPane` rather than `updateViewProperties` alone, for the two things that come with
     it. The export hook, so the PNG and slide paths read the canvas instead of cloning a DOM that
     carries none of its pixels. And the view *context*: `viewModifiers$` holds one for the whole
     studio and never clears it on a view change, so a view that publishes properties without one
     inherits the last view's - `viewActionsOptions` falls to its default and renders Zoom in /
     Zoom out / Optimize layout built from a stale context, wired to another view's handlers or to
     `DEFAULT_VIEW_CONTEXT`'s noops. Registering leaves all three null, which is what hides them. */
  useEffect(() => {
    registerViewPane({
      viewId: ViewIds.CONTOUR,
      viewProperties: {
        viewSettings,
        numericFields,
        i18nLocale,
        isEmptyView: isViewEmpty,
        isDrawable,
        viewInstanceId,
      } satisfies ContourViewProperties,
      isLegendActive: viewSettings.isLegendActive,
      getContainer: () => container,
      createExportCanvas: () => createExportCanvasRef.current?.() ?? null,
      viewInstanceId,
    });
  }, [
    viewSettings,
    numericFields,
    i18nLocale,
    isViewEmpty,
    isDrawable,
    viewInstanceId,
    container,
  ]);

  /* The one tooltip string that is prose rather than a field name or a number, so the chart
     cannot build it from what it already has. `@ardoq/xyz-charts` has no locale of its own. */
  /* Worded for this chart rather than reusing the surface's doq, whose copy says "for a surface"
     and "A surface is interpolated". Same shape as the `tooFewPositions` copy below it. */
  const noAreaCopy = useMemo(
    () => ({
      title: t(
        i18nLocale,
        'views:contour.emptyState.noArea.title',
        'These components do not spread far enough for contours'
      ),
      explanation: t(
        i18nLocale,
        'views:contour.emptyState.noArea.message1',
        'Contours are interpolated between the positions your components occupy. These do not enclose an area, so there is nothing to interpolate across.'
      ),
      remedy: t(
        i18nLocale,
        'views:contour.emptyState.noArea.message2',
        'Choose fields that spread your components across the plane.'
      ),
    }),
    [i18nLocale]
  );

  /* The whole accessible name, built here because the chart package has no locale. With no legend,
     this sentence is the only place the chart says that colour carries a field - so left in English
     it is the half of the no-legend story that does not survive translation. */
  /* The whole accessible name, built here because the chart package has no locale. With no legend,
     this sentence is the only place the chart says that colour carries a field - so left in English
     it is the half of the no-legend story that does not survive translation. Typed from the prop
     rather than restated, so the shape cannot drift from the package's. */
  const formatAccessibleName = useMemo<
    ContourChartProps['formatAccessibleName']
  >(
    () =>
      ({ pointCount, xTitle, yTitle, zTitle }) =>
        t(
          i18nLocale,
          'views:contour.accessibleName',
          { count: pointCount, x: xTitle, y: yTitle, z: zTitle },
          'Contour chart, {{count}} components. {{x}} across, {{y}} up, {{z}} as color.'
        ),
    [i18nLocale]
  );

  const formatCoincidentCount = useMemo(
    () => (count: number) =>
      t(
        i18nLocale,
        'views:contour.coincidentComponents',
        { count },
        '{{count}} components here'
      ),
    [i18nLocale]
  );

  const format = useMemo(
    () => (value: number) => formatNumber(value, 'default', locale),
    [locale]
  );

  /* Rendered by both branches rather than only the chart one, and that is the whole point of the
     live region below. Most screen readers announce a change inside a region that was already in
     the accessibility tree, so a region arriving together with its first message announces nothing.
     Nothing is preselected here, so every user reaches the chart by picking the last field - which
     is the moment `isViewEmpty` flips and, if any component lacks a value, the moment the first
     message exists. Under the same `ContourLayout` at the same position in both, so React
     reconciles them as one element and the region survives the switch.

     The legend and the padding stay gated: neither means anything before there is a chart. */
  /* What the color means. The chart names its two axes and has nowhere to say this, and a map
     whose legend is missing is a map of an unnamed quantity. */
  const colorLegend =
    coloring.kind === 'height'
      ? t(
          i18nLocale,
          'views:contour.colorLegend',
          { field: valueTitle },
          'Color shows {{field}}'
        )
      : t(
          i18nLocale,
          'views:contour.colorLegendFormatting',
          { field: valueTitle },
          'Contour lines show {{field}}. Color shows conditional formatting.'
        );

  const exclusionNotice = t(
    i18nLocale,
    'views:contour.exclusionNotice',
    {
      plotted: points.length,
      total: points.length + excludedCount,
    },
    '{{plotted}} of {{total}} components plotted. The rest have no value for one of the chosen fields.'
  );

  /* The same two lines the header shows, for the exported file - which is the canvas and nothing
     else, so a caption living only in this DOM would not survive it. Empty while the view is,
     matching the header: there is no chart to caption. */
  const exportCaptions = useMemo(
    () =>
      isViewEmpty
        ? []
        : [colorLegend, ...(excludedCount > 0 ? [exclusionNotice] : [])],
    [isViewEmpty, colorLegend, exclusionNotice, excludedCount]
  );

  const header = (
    <FlexBox
      padding={isViewEmpty ? 'none' : 'small'}
      paddingBottom="none"
      gap="medium"
    >
      {!isViewEmpty && (
        <Text color="textSubtle" variant="caption">
          {colorLegend}
        </Text>
      )}
      <FlexBox role="status" aria-live="polite">
        {excludedCount > 0 && !isViewEmpty && (
          <Text color="textSubtle" variant="caption">
            {exclusionNotice}
          </Text>
        )}
      </FlexBox>
    </FlexBox>
  );

  if (isViewEmpty) {
    return (
      <ContourLayout ref={setContainer}>
        {header}
        <ChartEmptyState
          emptyReason={emptyReason}
          noFieldsSelected={{
            title: t(
              i18nLocale,
              'views:contour.emptyState.noFieldsSelected.title',
              'Choose the fields to plot'
            ),
            message: t(
              i18nLocale,
              'views:contour.emptyState.noFieldsSelected.message',
              'This view reads three numeric fields: one for the X axis, one for the Y axis, and one for the value the color shows. Pick a field for each in the settings bar above.'
            ),
          }}
          noComponents={{
            title: t(
              i18nLocale,
              'views:contour.emptyState.noComponentsInView.title',
              'There is nothing in view to plot'
            ),
            message: t(
              i18nLocale,
              'views:contour.emptyState.noComponentsInView.message',
              'No components are in view, so there are no positions to build contours from.'
            ),
          }}
          tooFewPositions={{
            title: t(
              i18nLocale,
              'views:contour.emptyState.tooFewPositions.title',
              'Not enough distinct positions to draw contours'
            ),
            explanation: t(
              i18nLocale,
              'views:contour.emptyState.tooFewPositions.message1',
              'Contours are interpolated between the positions your components occupy, and these share fewer than three, so there is no area to interpolate across.'
            ),
            remedy: t(
              i18nLocale,
              'views:contour.emptyState.tooFewPositions.message2',
              'Choose fields that spread the components across the plane, or switch to the 3D Scatter view, which plots this data as it is.'
            ),
          }}
        />
      </ContourLayout>
    );
  }

  return (
    <ContourLayout ref={setContainer}>
      {header}
      <ChartArea>
        <ViewLegendContainer visible={viewSettings.isLegendActive}>
          <ViewLegend
            componentTypes={[]}
            referenceTypes={[]}
            additionalRows={legendRows}
            activeDiffMode={null}
            itemClickTrackingFunction={noop}
            labels={viewLegendLabels}
          />
        </ViewLegendContainer>
        <ContourChart
          points={points}
          axes={axes}
          xTitle={xAxisTitle}
          yTitle={yAxisTitle}
          zTitle={valueTitle}
          interpolation={viewSettings.interpolation}
          aggregation={viewSettings.aggregation}
          resolution={viewSettings.resolution}
          ramp={viewSettings.ramp}
          bands={viewSettings.bands}
          coloring={coloring}
          contourCount={viewSettings.contourCount}
          style={{
            showFilledBands: viewSettings.showFilledBands,
            showIsolines: viewSettings.showIsolines,
            showSamplePositions: viewSettings.showSamplePositions,
          }}
          format={format}
          formatCoincidentCount={formatCoincidentCount}
          formatAccessibleName={formatAccessibleName}
          onExportCanvasAvailable={onExportCanvasAvailable}
          exportCaptions={exportCaptions}
          onDrawableChange={setIsDrawable}
          /* The source data catches the two cases it can see - fewer than three positions, and all
             of them on a line - and only for `linear`, which is the only kernel they stop. The
             gridding has one more the app cannot: a triangulation with area that still lays no cell
             centre inside any of its triangles. `ContourChart` renders this instead of its canvas
             when the grid comes back empty, so without it the view is the container's background,
             no message, and a settings bar still showing three chosen fields. */
          emptyState={<TooFewPositionsDoq copy={noAreaCopy} />}
          {...chartColors}
        />
      </ChartArea>
    </ContourLayout>
  );
};
